import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import _ from 'lodash';
import moment from 'moment/moment';

import { useStyles } from '~/assets/styles';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Typography from '~/components/core/Atomic/Typography';
import RadioWithButtonWrapper from '~/components/core/RadioWithButtonWrapper';
import { useCms } from '~/components/hooks/useCms';
import ClosedExposureExpensesLimitDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/ClosedExposureLimitDialog/ClosedExposureExpensesLimitDialog';
import ClosedExposureIndemnityLimitDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/ClosedExposureLimitDialog/ClosedExposureIndemnityLimitDialog';
import OfacScreenConfigurationDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/OfacScreenConfigurationDialog';
import SanctionsScreenConfigurationDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/SanctionsScreenConfigurationDialog';
import SelectManagersConfigurationDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/SelectManagersConfigurationDialog';
import TfnMandatoryCoveragesDialog from '~/components/SystemConfiguration/Tabs/PaymentsConfiguration/PaymentConfigTab/AdditionalDialogs/TfnMandatoryCoveragesDialog/TfnMandatoryCoveragesDialog';
import {
  CONFIGURATION_FEATURES_NAMES,
  DUPLICATE_PAYMENTS_VERIFICATION_ACTION,
  PAYMENT_METHODS_DETAILS_DICT,
} from '~/Types';
import { isFeatureEnabled, isOrganizationAuOrg, reportAxiosError } from '~/Utils';

import Text from '../../../..//core/TextComponents/Text/index';
import CardDialog from '../../../../CardDialog';
import WithConfirm from '../../../../ConfirmModal';
import { FsTooltip, LoadingSwitch, SortableTable } from '../../../../core';
import useOrganization from '../../../../OrganizationContext';
import useLobOptions from '../../../../TPA/LOB/hooks/useLobOptions';
import useDataFetcher from '../../../../useDataFetcher';
import { useSysconfig } from '../../../SystemConfigurationScreen';
import PaymentsConfigurationForm from '../PaymentsConfigurationForm';

import CapRecoveriesConfigurationDialog, {
  CAP_RECOVERIES_MODE_FORM_FIELD,
} from './AdditionalDialogs/CapRecoveriesConfigurationDialog';
import EditDuplicatePaymentsConfigurationDialog from './AdditionalDialogs/EditDuplicatePaymentsConfigurationDialog';
import GeneralRecoveriesConfigurationDialog from './AdditionalDialogs/GeneralRecoveriesConfigurationDialog';
import IncludeVatConfigurationDialog from './AdditionalDialogs/IncludeVatConfigurationDialog';
import MultiCurrencyConfigurationDialog from './AdditionalDialogs/MultiCurrencyConfigurationDialog';
import PresentApprovalScreenConfigurationDialog from './AdditionalDialogs/PresentApprovalScreenConfigurationDialog';
import RecoveryRequestsConfigurationDialog from './AdditionalDialogs/RecoveryRequestsConfigurationDialog';
import RequireApprovalConfigurationDialog from './AdditionalDialogs/RequireApprovalConfigurationDialog';
import ExpensesSubReservesContainer from './SubReservesTypesDialog/ExpensesSubReservesContainer';
import ConfigSwitch from './ConfigSwitch';
import {
  CAP_RECOVERIES_MODE_OPTIONS,
  CAP_RECOVERIES_MODES,
  CONFIG_PER_VALUES,
  isCapEnforcementOn,
  PAYMENTS_CONFIG_FIELDS,
} from './constants';
import PaymentMethodSpecialCell from './PaymentMethodSpecialCell';
import RecoveryRequestSpecialCell, { findOwnRecoveryRequestsConfig } from './RecoveryRequestSpecialCell';
import { getUserIdsWithImplicitApprovalPermission, isRecordOnlyEnabledForOrg } from './utils';

import styles from '../PaymentsConfigurationForm.module.scss';

const ADJUSTERS_NUM_APPROVAL_SCREEN_CONFIG = 7;

// Helper function to check if multi-currency configuration has any enabled currencies
const hasEnabledMultiCurrencyConfiguration = (paymentsConfiguration, organization) => {
  const config = paymentsConfiguration?.multi_currency_configuration;
  if (!config) return false;

  if (!paymentsConfiguration.allow_claim_multi_currency) {
    return false;
  }

  if (!organization.sub_organizations_enabled) {
    // Check organization level configuration - must have currencies
    return config.organization_currencies && config.organization_currencies.length > 0;
  } else {
    // Check sub-organization level configuration - must have at least one enabled sub-org
    return (
      config.sub_organizations &&
      config.sub_organizations.some(
        (subOrgConfig) => subOrgConfig.enabled && subOrgConfig.currencies && subOrgConfig.currencies.length > 0
      )
    );
  }
};

// Helper function to display configured currencies
const getConfiguredCurrenciesDisplay = (paymentsConfiguration, organization) => {
  const config = paymentsConfiguration?.multi_currency_configuration;
  if (!config) return 'None';

  if (!organization.sub_organizations_enabled) {
    // Organization-level configuration
    if (config.organization_currencies) {
      return config.organization_currencies.join(', ') || 'None';
    }
    return 'None';
  } else {
    if (_.isEmpty(config.sub_organizations)) return 'None';

    const enabledSubOrgs = [];
    const totalCurrencies = new Set();

    config.sub_organizations.forEach((subOrgConfig) => {
      if (subOrgConfig.enabled && subOrgConfig.currencies) {
        enabledSubOrgs.push(subOrgConfig.sub_organization_id);
        subOrgConfig.currencies.forEach((currency) => totalCurrencies.add(currency));
      }
    });

    if (enabledSubOrgs.length === 0) return 'None';

    return `${enabledSubOrgs.length} sub-orgs enabled (${totalCurrencies.size} unique currencies)`;
  }
};

const PaymentsConfigTab = ({ processIncomingConfigValues, processOutgoingConfigValues }) => {
  const { organization, users } = useSysconfig();
  // The signed-in user's own organization, not the one being configured.
  const { userOrganization: nonImpersonatedOrganization } = useCms();
  // organization-context-usage.mdc routes complex org data to useSysconfig(), but AdminPage's
  // sysconfig route mounts this provider with the organization being configured
  // (AdminPage.jsx:59), so useOrganization() resolves to the target org here — the same reason
  // subOrganizations is already read from it on this line.
  const { subOrganizations, coverageConfiguration } = useOrganization();
  const { classes } = useStyles();
  const vatLabel = isOrganizationAuOrg(organization) ? 'GST' : 'VAT';

  const usersDict = _.keyBy(users, 'id');

  const { isLoading, isError, data, reloadData } = useDataFetcher(
    `/sysconfig/api/v1/organizations/${organization.id}/payments_configuration`
  );

  const paymentsConfiguration = data?.payments_configuration;
  const isConfigPerOrg = paymentsConfiguration?.is_org_level_configuration;
  const paymentsMethodConfigurations = paymentsConfiguration?.payment_method_configurations || [];
  // getRows() collapses to a single organization row unless sub-orgs are both enabled and the
  // configuration axis, so this is what makes a row's id a sub-organization id.
  const isPerSubOrgRow = organization.sub_organizations_enabled && !isConfigPerOrg;

  const isRecoveryRequestsEnabled = isFeatureEnabled(organization, CONFIGURATION_FEATURES_NAMES.RECOVERY_REQUESTS);
  // shouldFetch, not just a hidden column: an organization without the feature pays for neither GET.
  const { data: recoveryRequestsConfigs, reloadData: reloadRecoveryRequestsConfigs } = useDataFetcher(
    `/sysconfig/api/v1/organizations/${organization.id}/recovery_requests/configs`,
    null,
    { shouldFetch: isRecoveryRequestsEnabled }
  );
  const { data: availableRecoveryProviders } = useDataFetcher(
    `/sysconfig/api/v1/organizations/${organization.id}/recovery_requests/available_providers`,
    null,
    { shouldFetch: isRecoveryRequestsEnabled }
  );

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isGlobalPaymentsSubmitting, setIsGlobalPaymentsSubmitting] = useState(false);
  const [activePaymentMethodConfig, setActivePaymentMethodConfig] = useState(null);
  const [isNewPaymentMethodConfig, setIsNewPaymentMethodConfig] = useState(false);
  // undefined means the dialog is closed; null means the organization-level scope.
  const [recoveryRequestsDialogScope, setRecoveryRequestsDialogScope] = useState(undefined);

  const commerceBankEnabled = !!_.find(
    paymentsMethodConfigurations,
    (config) => config.payment_method === 'commerce_bank' && !config.is_disabled
  );
  // Five Sigma staff set up an organization's first configuration of a legacy method from its column's
  // "Set" link, so they always see it; the organization's own users see it once one exists.
  const isLegacyMethodColumnShown = (paymentMethod) =>
    Boolean(nonImpersonatedOrganization?.is_super_organization) ||
    paymentsMethodConfigurations.some((methodConfig) => methodConfig.payment_method === paymentMethod);

  const { lobOptions, isLoading: isLobLoading } = useLobOptions({
    subOrganizationIds: [],
  });
  const isHomeLobEnabledForOrg = lobOptions?.includes('home_claim');
  const paymentProvidersConfigured = _.compact([
    data?.additional_details?.is_vitesse_configured ? 'vitesse' : null,
    data?.additional_details?.is_stripe_configured ? 'stripe' : null,
  ]);

  const handleOpenDialog = (paymentMethodConfig) => {
    setActivePaymentMethodConfig(paymentMethodConfig);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsNewPaymentMethodConfig(false);
    setIsDialogOpen(false);
  };

  const handleRecoveryRequestsSubmit = async (params) => {
    try {
      await axios.put(`/sysconfig/api/v1/organizations/${organization.id}/recovery_requests/config`, params);
      await reloadRecoveryRequestsConfigs();
      setRecoveryRequestsDialogScope(undefined);
    } catch (error) {
      await reportAxiosError(error);
      // Rethrown so the dialog can clear its submitting flag and stay open for a retry — the same
      // contract handlePaymentsConfigUpdate's throwOnError gives the sibling dialogs' Formik.
      throw error;
    }
  };

  const getLastUpdatedDate = (columnRow) => {
    let datesArray;

    if (isConfigPerOrg) {
      datesArray = paymentsMethodConfigurations?.map((paymentMethodConfig) => paymentMethodConfig.last_updated);
    } else {
      datesArray = paymentsMethodConfigurations
        ?.filter((paymentMethodConfig) => paymentMethodConfig.sub_organization_id === columnRow.id)
        .map((paymentMethodConfig) => paymentMethodConfig.last_updated);
    }

    return datesArray.length > 0 ? moment(_.max(datesArray)).format('DD/MM/YYYY HH:mm') : null;
  };

  const getColumns = () => {
    const columnData = [
      {
        id: 'name',
        label: isConfigPerOrg ? 'Org Name' : 'Sub-org Name',
      },
      {
        id: 'last_updated',
        label: 'Last Updated',
        specialCell: getLastUpdatedDate,
      },
    ];

    Object.keys(PAYMENT_METHODS_DETAILS_DICT)
      .filter((key) => {
        let res = true;
        if (PAYMENT_METHODS_DETAILS_DICT[key].is_legacy_method && !isLegacyMethodColumnShown(key)) {
          res = false;
        }
        if (key === 'one_inc' && !data?.additional_details?.is_one_inc_configured) {
          res = false;
        }
        return res;
      })
      .map((key) => {
        columnData.push({
          id: key,
          label: `${PAYMENT_METHODS_DETAILS_DICT[key].display_name} Method`,
          disableSort: true,
          width: '12.5%',
          specialCell: (columnRow) => (
            <PaymentMethodSpecialCell
              orgId={organization.id}
              subOrgId={isConfigPerOrg ? undefined : columnRow.id}
              methodName={key}
              paymentsMethods={paymentsMethodConfigurations}
              handleOpenDialog={handleOpenDialog}
              handleIsNewPaymentMethodConfig={setIsNewPaymentMethodConfig}
              isDisabled={isGlobalPaymentsSubmitting}
            />
          ),
        });
      });

    // Appended after the payment-method map, never added into PAYMENT_METHODS_DETAILS_DICT: that
    // dict is what PaymentMethodConfiguration.payment_method validates against, so an entry there
    // would surface recovery requests as a payable method throughout the UI. Sharing this table is
    // a UI decision; it does not make this a payment method.
    if (isRecoveryRequestsEnabled) {
      columnData.push({
        id: 'recovery_requests',
        label: 'Recovery Requests',
        disableSort: true,
        width: '12.5%',
        specialCell: (columnRow) => (
          <RecoveryRequestSpecialCell
            subOrgId={isPerSubOrgRow ? columnRow.id : undefined}
            configs={recoveryRequestsConfigs || []}
            availableProviders={availableRecoveryProviders || []}
            onEdit={setRecoveryRequestsDialogScope}
            isDisabled={isGlobalPaymentsSubmitting}
          />
        ),
      });
    }

    return columnData;
  };

  const getRows = () => {
    if (!organization.sub_organizations_enabled || isConfigPerOrg) {
      return [organization];
    }
    return subOrganizations;
  };

  const handlePaymentsConfigUpdate = async (payload, throwOnError = false) => {
    try {
      setIsGlobalPaymentsSubmitting(true);
      await axios.patch(`/sysconfig/api/v1/organizations/${organization.id}/payments_configuration`, payload);
      await reloadData();
    } catch (error) {
      await reportAxiosError(error);

      if (throwOnError) {
        throw error;
      }
    } finally {
      setIsGlobalPaymentsSubmitting(false);
    }
  };

  const capRecoveriesMode = paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.CAP_RECOVERIES_AT_TOTAL_PAID];
  const isCapRecoveriesOn = isCapEnforcementOn(capRecoveriesMode);
  const capRecoveriesLabel = CAP_RECOVERIES_MODE_OPTIONS.find((opt) => opt.optionValue === capRecoveriesMode)?.text;

  const handleCapRecoveriesUpdate = (payload, fromDialog = false) => {
    const newMode = fromDialog ? payload[CAP_RECOVERIES_MODE_FORM_FIELD] : CAP_RECOVERIES_MODES.OFF;
    return handlePaymentsConfigUpdate({ [PAYMENTS_CONFIG_FIELDS.CAP_RECOVERIES_AT_TOTAL_PAID]: newMode }, fromDialog);
  };

  const handleFormSubmit = async (values, methodName) => {
    try {
      const payload = { ...activePaymentMethodConfig, ...processOutgoingConfigValues(values) };
      if (isNewPaymentMethodConfig) {
        await axios.post(
          `/sysconfig/api/v1/organizations/${organization.id}/payments_configuration/${methodName}`,
          payload
        );
        setIsNewPaymentMethodConfig(false);
      } else {
        await axios.put(
          `/sysconfig/api/v1/organizations/${organization.id}/payments_configuration/${methodName}`,
          payload
        );
      }
      await reloadData();
      setIsDialogOpen(false);
    } catch (e) {
      await reportAxiosError(e);
    }
  };

  const validateIsLastEnabledPaymentMethod = () => {
    let basePaymentMethodConfigsArray = paymentsMethodConfigurations;
    if (!isConfigPerOrg) {
      basePaymentMethodConfigsArray = basePaymentMethodConfigsArray.filter(
        (paymentMethodConfig) =>
          paymentMethodConfig.sub_organization_id === activePaymentMethodConfig?.sub_organization_id
      );
    }
    return !basePaymentMethodConfigsArray
      .filter((paymentMethodConfig) => paymentMethodConfig.payment_method !== activePaymentMethodConfig?.payment_method)
      .some((paymentMethodConfig) => !paymentMethodConfig.is_disabled);
  };

  const approvingUserIds = Array.from(
    new Set(
      (paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.ADDITIONAL_APPROVING_USERS] || []).concat(
        getUserIdsWithImplicitApprovalPermission(users)
      )
    )
  );
  const submittingUserIds = paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.ADDITIONAL_SUBMITTING_USER_IDS] || [];

  // the raw stored flag, not the effective value the display surfaces derive from it, so the card
  // summary reflects what is actually stored
  const isDisplayNetAmountsOn = !!paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.DISPLAY_NET_AMOUNTS];
  const isReduceReservesByNetPaidOn = !!paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.REDUCE_RESERVES_BY_NET_PAID];
  const isRecordOnlyEnabled = isRecordOnlyEnabledForOrg(paymentsConfiguration, organization);
  const isSkipApprovalForRecordOnlyOn =
    !!paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.SKIP_APPROVAL_FOR_RECORD_ONLY_PAYMENTS];

  return (
    <CardDialog title="Payments Configuration" containerClassName={styles.paymentsV3Container}>
      <LoadingSwitch isLoading={isLoading || isLobLoading} isError={isError}>
        <Typography variant="body1">Manage payment methods, configurations and settings</Typography>
        <Grid container spacing={2} className={styles.switchesContainer}>
          <ConfigSwitch
            title="Allow expense payments in a closed exposure"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_PAYING_EXPENSES_IF_EXPOSURE_IS_CLOSED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={ClosedExposureExpensesLimitDialog}
          >
            {paymentsConfiguration && paymentsConfiguration.is_expenses_limit_for_closed_exposure_enabled ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Payment limit: {paymentsConfiguration.expenses_limit_for_closed_exposure}
                </Text>
              </div>
            ) : null}
          </ConfigSwitch>

          <ConfigSwitch
            title="Allow indemnity payments in a closed exposure"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_PAYING_INDEMNITY_IF_EXPOSURE_IS_CLOSED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={ClosedExposureIndemnityLimitDialog}
          >
            {paymentsConfiguration && paymentsConfiguration.is_indemnity_limit_for_closed_exposure_enabled ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Payment limit: {paymentsConfiguration.indemnity_limit_for_closed_exposure}
                </Text>
              </div>
            ) : null}
          </ConfigSwitch>

          <ConfigSwitch
            title="Allow expense payments when coverage issue exists"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_PAYING_EXPENSES_IF_COVERAGE_ISSUE_EXISTS}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />

          <ConfigSwitch
            title="Pre-fill indemnity payee with claimant details"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.PREFILL_INDEMNITY_PAYEE_WITH_CLAIMANT}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Auto-fill Mail To from primary payee"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_PAYMENT_METHOD_AUTO_FILL_MAIL_TO_FROM_PAYEE}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Add reserve pre-fill capability to payments"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_PREFILL_RESERVE_AMOUNTS_CAPABILITY_ENABLED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Require approval for every payment"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.REQUIRE_APPROVAL}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={RequireApprovalConfigurationDialog}
          >
            {paymentsConfiguration?.enable_trusted_payee_payments ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Trusted payee payments: Enabled
                </Text>
              </div>
            ) : null}
            {isRecordOnlyEnabled && isSkipApprovalForRecordOnlyOn ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Skip approval for record-only payments: Enabled
                </Text>
              </div>
            ) : null}
          </ConfigSwitch>

          <ConfigSwitch
            title="Require verification for contact financial details edit"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.REQUIRE_VERIFICATION_FOR_CONTACT_FINANCE_EDIT}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />

          <ConfigSwitch
            title="Require verification for contact AU TFN declaration edit"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.REQUIRE_VERIFICATION_FOR_CONTACT_AU_TFN_EDIT}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={TfnMandatoryCoveragesDialog}
          >
            {paymentsConfiguration?.contact_au_tfn_mandatory_coverages?.length ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Mandatory TFN coverages: {paymentsConfiguration.contact_au_tfn_mandatory_coverages.length}
                </Text>
              </div>
            ) : null}
          </ConfigSwitch>

          <ConfigSwitch
            title="Allow indemnity payment in damaged property exposures only when the property owner is defined"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ALLOW_INDEMNITY_PAYMENTS_ONLY_FOR_EXISTING_PROPERTY_OWNER}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />

          <ConfigSwitch
            title="Present approval screen"
            hasAdditionalFields
            AdditionalFieldsDialog={PresentApprovalScreenConfigurationDialog}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.PRESENT_APPROVAL_SCREEN}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          >
            <div>
              Users with approval permissions:
              <div>
                {approvingUserIds.slice(0, ADJUSTERS_NUM_APPROVAL_SCREEN_CONFIG).map((user_id) => (
                  <Chip key={user_id} size="small" label={usersDict[user_id].username} className={classes.chip} />
                ))}
                {approvingUserIds.length > ADJUSTERS_NUM_APPROVAL_SCREEN_CONFIG ? (
                  <FsTooltip
                    title={approvingUserIds
                      .slice(ADJUSTERS_NUM_APPROVAL_SCREEN_CONFIG)
                      .map((user_id) => usersDict[user_id].username)
                      .join(', ')}
                    placement="top"
                  >
                    <span className="pl-12 font-bold text-teal-700">
                      {' '}
                      +{approvingUserIds.length - ADJUSTERS_NUM_APPROVAL_SCREEN_CONFIG}
                    </span>
                  </FsTooltip>
                ) : null}
              </div>
            </div>
          </ConfigSwitch>

          <ConfigSwitch
            title="Allow multiple pending payment requests"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ALLOW_MULTIPLE_PENDING_PAYMENT_REQUESTS}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />

          <ConfigSwitch
            title="Enable duplicate verification"
            isInitialized={paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.DUPLICATE_PAYMENTS_VERIFICATION_INITIALIZED]}
            hasAdditionalFields
            AdditionalFieldsDialog={EditDuplicatePaymentsConfigurationDialog}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_DUPLICATE_PAYMENTS_VERIFICATION}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          >
            {paymentsConfiguration && (
              <div>
                <div>
                  Identify duplicate payments per:{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {paymentsConfiguration.duplicate_payments_level_of_verification === 'claim' ? 'Claim' : 'Exposure'}
                  </span>
                </div>
                <div>
                  When a duplicate payment is identified:{' '}
                  <span style={{ fontWeight: 'bold' }}>
                    {DUPLICATE_PAYMENTS_VERIFICATION_ACTION[paymentsConfiguration.duplicate_payments_action]?.desc}
                  </span>
                </div>
              </div>
            )}
          </ConfigSwitch>
          <ConfigSwitch
            title={`Include ${vatLabel} in payment and recovery screens`}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.INCLUDE_VAT_IN_PAYMENT_SCREEN}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={IncludeVatConfigurationDialog}
          >
            {isDisplayNetAmountsOn ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Display net amounts: Enabled
                </Text>
              </div>
            ) : null}
            {isReduceReservesByNetPaidOn ? (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Reduce reserves by Net paid: Enabled
                </Text>
              </div>
            ) : null}
          </ConfigSwitch>
          <ConfigSwitch
            title={`Show ${vatLabel} columns in finances summary`}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.INCLUDE_VAT_IN_FINANCES_SUMMARY}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Enable 1099 fields"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_1099_FIELDS}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Require TIN for indemnity payees"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.REQUIRE_TIN_FOR_INDEMNITY_PAYEES}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Enable Expenses Sub Reserves"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_EXPENSES_SUB_RESERVES_CONFIG_ENABLED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={ExpensesSubReservesContainer}
            isInitialized={!_.isEmpty(paymentsConfiguration?.expenses_sub_reserves_config)}
          />
          <ConfigSwitch
            title="Multiple Sub Reserves Per Payment"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_MULTIPLE_SUB_RESERVE_PAYMENTS_ENABLED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            isInitialized={!_.isEmpty(paymentsConfiguration?.is_multiple_sub_reserve_payments_enabled)}
          />

          {commerceBankEnabled && (
            <ConfigSwitch
              title="Present payment submission screen"
              hasAdditionalFields
              AdditionalFieldsDialog={PresentSubmissionScreenConfigurationDialog}
              paymentsConfiguration={paymentsConfiguration}
              configKey={PAYMENTS_CONFIG_FIELDS.PRESENT_SUBMISSION_SCREEN}
              onChange={handlePaymentsConfigUpdate}
              disabled={isGlobalPaymentsSubmitting}
            >
              <div>
                Users with submission permissions:
                <div>
                  {submittingUserIds.map((user_id) => (
                    <Chip key={user_id} size="small" label={usersDict[user_id].username} className={classes.chip} />
                  ))}
                </div>
              </div>
            </ConfigSwitch>
          )}
          <ConfigSwitch
            title="Disable payment above reserve"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.SHOULD_DISABLE_PAYMENT_EXCEEDING_RESERVE}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Enable Uk Bank Account Validation"
            isInitialized={paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.ENABLE_UK_BANK_ACCOUNT_VALIDATION]}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_UK_BANK_ACCOUNT_VALIDATION}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Applied deductible will be deducted from outstanding reserves "
            isInitialized={paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.ADJUST_RESERVE_INCLUDES_DEDUCTIBLE]}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ADJUST_RESERVE_INCLUDES_DEDUCTIBLE}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Adjust reserve after payment cancellation"
            isInitialized={paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.ADJUST_RESERVE_AFTER_PAYMENT_CANCELLATION]}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ADJUST_RESERVE_AFTER_PAYMENT_CANCELLATION}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Sanctions IO Configuration"
            isSelected={paymentsConfiguration?.enable_sanctions_io_integration}
            isInitialized={paymentsConfiguration?.enable_sanctions_io_integration}
            hasAdditionalFields
            AdditionalFieldsDialog={SanctionsScreenConfigurationDialog}
            paymentsConfiguration={paymentsConfiguration}
            configKey="enable_sanctions_io_integration"
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="OFAC Configuration"
            isSelected
            hasAdditionalFields
            AdditionalFieldsDialog={OfacScreenConfigurationDialog}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.OFAC_CONFIGURATION_ENABLED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Include anticipated amount to the total recoveries calculation"
            isInitialized={
              paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.IS_ANTICIPATED_AMOUNT_INCLUDED_IN_RECOVERIES_CALC]
            }
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_ANTICIPATED_AMOUNT_INCLUDED_IN_RECOVERIES_CALC}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Include recoveries in the incurred calculation"
            isInitialized={paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.IS_RECOVERIES_INCLUDED_IN_INCURRED_CALC]}
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_RECOVERIES_INCLUDED_IN_INCURRED_CALC}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Disable the Adjuster from modifying self payment limits"
            isInitialized={
              paymentsConfiguration?.[PAYMENTS_CONFIG_FIELDS.DISABLE_ADJUSTER_MODIFYING_SELF_PAYMENT_LIMITS]
            }
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.DISABLE_ADJUSTER_MODIFYING_SELF_PAYMENT_LIMITS}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />
          <ConfigSwitch
            title="Allow claim multi-currency"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ALLOW_CLAIM_MULTI_CURRENCY}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={MultiCurrencyConfigurationDialog}
            isInitialized={hasEnabledMultiCurrencyConfiguration(paymentsConfiguration, organization)}
            isSelected={
              paymentsConfiguration?.allow_claim_multi_currency &&
              hasEnabledMultiCurrencyConfiguration(paymentsConfiguration, organization)
            }
          >
            {paymentsConfiguration?.allow_claim_multi_currency && (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Configured currencies: {getConfiguredCurrenciesDisplay(paymentsConfiguration, organization)}
                </Text>
              </div>
            )}
          </ConfigSwitch>

          <ConfigSwitch
            title="Enable General Recoveries"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.IS_GENERAL_RECOVERIES_ENABLED}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={GeneralRecoveriesConfigurationDialog}
            isInitialized={!_.isEmpty(paymentsConfiguration?.general_recoveries_configuration)}
          >
            {paymentsConfiguration?.is_general_recoveries_enabled && (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Recovery Type Field:{' '}
                  {paymentsConfiguration?.general_recoveries_configuration?.general_recovery_type?.active
                    ? 'Enabled'
                    : 'Disabled'}
                </Text>
              </div>
            )}
          </ConfigSwitch>

          <ConfigSwitch
            title="Enable Split General Recoveries"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.ENABLE_SPLIT_GENERAL_RECOVERY}
            onChange={handlePaymentsConfigUpdate}
            disabled={isGlobalPaymentsSubmitting}
          />

          <ConfigSwitch
            title="Cap Recoveries at Total Paid"
            paymentsConfiguration={paymentsConfiguration}
            configKey={PAYMENTS_CONFIG_FIELDS.CAP_RECOVERIES_AT_TOTAL_PAID}
            onChange={handleCapRecoveriesUpdate}
            disabled={isGlobalPaymentsSubmitting}
            hasAdditionalFields
            AdditionalFieldsDialog={CapRecoveriesConfigurationDialog}
            isInitialized={isCapRecoveriesOn}
            isSelected={isCapRecoveriesOn}
          >
            {isCapRecoveriesOn && capRecoveriesLabel && (
              <div>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
                  Enforcement: {capRecoveriesLabel}
                </Text>
              </div>
            )}
          </ConfigSwitch>

          {coverageConfiguration?.is_capacity_agreement_enabled && (
            <ConfigSwitch
              title="Enable split routing"
              paymentsConfiguration={paymentsConfiguration}
              configKey={PAYMENTS_CONFIG_FIELDS.IS_SPLIT_ROUTING_ENABLED}
              onChange={handlePaymentsConfigUpdate}
              disabled={isGlobalPaymentsSubmitting}
            />
          )}
        </Grid>
        {organization.sub_organizations_enabled && (
          <div className={styles.configPerContainer}>
            <Typography variant="subtitle2">Set configuration per:</Typography>
            <div className={styles.radioButtonsContainer}>
              {CONFIG_PER_VALUES.map((configPerValue) => (
                <WithConfirm
                  key={configPerValue.value}
                  primaryButtonName="Set"
                  triggerMethod="onChange"
                  title="Are you sure you want to change the context?"
                  shouldCloseOnPrimary
                  contentText="Changing the context will immediately affect all payment configurations"
                >
                  <RadioWithButtonWrapper
                    name="payments_configuration_level"
                    text={configPerValue.text}
                    value={configPerValue.value}
                    checked={
                      (isConfigPerOrg && configPerValue.value === 'org') ||
                      (!isConfigPerOrg && configPerValue.value === 'suborgs')
                    }
                    onChange={() =>
                      handlePaymentsConfigUpdate({
                        [PAYMENTS_CONFIG_FIELDS.IS_ORG_LEVEL_CONFIGURATION]:
                          !paymentsConfiguration.is_org_level_configuration,
                      })
                    }
                    className={styles.radioButton}
                    disabled={isGlobalPaymentsSubmitting}
                  />
                </WithConfirm>
              ))}
            </div>
          </div>
        )}
        <SortableTable columns={getColumns()} rows={getRows()} />
        {isDialogOpen && (
          <PaymentsConfigurationForm
            isDialogOpen={isDialogOpen}
            onClose={handleCloseDialog}
            handleFormSubmit={handleFormSubmit}
            configValues={activePaymentMethodConfig ? processIncomingConfigValues(activePaymentMethodConfig) : null}
            isHomeLobEnabledForOrg={isHomeLobEnabledForOrg}
            paymentMethodName={activePaymentMethodConfig?.payment_method}
            isLastEnabledPaymentMethod={validateIsLastEnabledPaymentMethod()}
            paymentProvidersConfigured={paymentProvidersConfigured}
          />
        )}
        {recoveryRequestsDialogScope !== undefined && (
          <RecoveryRequestsConfigurationDialog
            configuredForName={
              recoveryRequestsDialogScope === null
                ? organization.name
                : subOrganizations?.find((subOrg) => subOrg.id === recoveryRequestsDialogScope)?.name ||
                  `Sub-organization ${recoveryRequestsDialogScope}`
            }
            subOrganizationId={recoveryRequestsDialogScope}
            // The scope's own row, never the resolved one: prefilling a sub-org from the org row it
            // follows would let Save write an override the operator never chose. inheritedConfig
            // carries that fallback separately, for the dialog to show and label as inherited.
            config={findOwnRecoveryRequestsConfig(
              recoveryRequestsConfigs || [],
              recoveryRequestsDialogScope ?? undefined
            )}
            inheritedConfig={
              recoveryRequestsDialogScope === null
                ? undefined
                : findOwnRecoveryRequestsConfig(recoveryRequestsConfigs || [], undefined)
            }
            availableProviders={availableRecoveryProviders || []}
            onSubmit={handleRecoveryRequestsSubmit}
            onCancel={() => setRecoveryRequestsDialogScope(undefined)}
          />
        )}
      </LoadingSwitch>
    </CardDialog>
  );
};

PaymentsConfigTab.propTypes = {
  processIncomingConfigValues: PropTypes.func.isRequired,
  processOutgoingConfigValues: PropTypes.func.isRequired,
};

const PresentSubmissionScreenConfigurationDialog = ({
  paymentsConfiguration,
  disabled = false,
  onCancel,
  onSubmit,
}) => {
  return (
    <SelectManagersConfigurationDialog
      paymentsConfiguration={paymentsConfiguration}
      disabled={disabled}
      onCancel={onCancel}
      onSubmit={onSubmit}
      configurationKey={PAYMENTS_CONFIG_FIELDS.ADDITIONAL_SUBMITTING_USER_IDS}
      cardTitle="Present payment submission screen"
      inputLabel="Users with submission permissions"
    />
  );
};

PresentSubmissionScreenConfigurationDialog.propTypes = {
  paymentsConfiguration: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
};

export default PaymentsConfigTab;
